// components/VendorPricing.jsx — the estimate's Material take-list, with vendor pricing:
//   • filter the material by section, tick items, assign the vendors you buy that group from (or "Default vendor")
//   • "Request prices" → one private link per vendor (e-mail it / copy it). SKUs on file for that vendor go with it.
//     The vendor types prices on the page or downloads it to Excel and uploads it back. No login for them.
//   • prices come back here: compare vendors per item, pick (or "lowest for all"), apply to the estimate.
//     Applied prices become each item's rate + vendor on the estimate, and go to the budget when it's published.
//   • a vendor who e-mails a spreadsheet instead: "Enter prices" → import their Excel or type them in.
//
// Props: rows (R.takeList), mappings, catalogById, ccIndex, vendorPricing (settings.vendorPricing), setVendorPricing,
//        onApply(awards, vendorPricing), takeoffId, ownerKind, ownerId, jobName, companyName, apiBase, money, n0

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";

const cls = (...c) => c.filter(Boolean).join(" ");
const idOf = (v) => (v && typeof v === "object" ? String(v._id || "") : v ? String(v) : "");
const keyOf = (t) => `${t.itemCode}||${t.size || ""}||${t.unit || ""}`;
const matchKey = (code, size) => `${code}||${size || ""}`;
const money2 = (v) =>
  v == null || v === "" || !Number.isFinite(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 4 });
const moneyT = (v) =>
  v == null || !Number.isFinite(Number(v)) ? "—" : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" });
const vendorLabel = (v) => v?.companyName || v?.name || v?.displayName || "Vendor";
const vendorEmail = (v) =>
  v?.email ||
  v?.contactEmail ||
  v?.primaryContact?.email ||
  v?.accountsPayableEmail ||
  (Array.isArray(v?.contacts) ? (v.contacts.find((c) => c?.email) || {}).email : "") ||
  "";
const inDays = (n) => {
  const d = new Date();
  let k = 0;
  while (k < n) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) k++;
  }
  return d.toISOString().slice(0, 10);
};
const day = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");

const STATUS = {
  draft: ["Not sent", "bg-white/10 text-gray-300"],
  sent: ["Sent", "bg-orange-500/15 text-orange-300"],
  viewed: ["Opened", "bg-sky-500/15 text-sky-300"],
  submitted: ["Prices in", "bg-emerald-500/15 text-emerald-300"],
  closed: ["Closed", "bg-white/5 text-gray-500"],
};

export default function VendorPricing({
  rows = [],
  mappings = [],
  catalogById = new Map(),
  ccIndex = null,
  vendorPricing,
  setVendorPricing,
  onApply,
  takeoffId,
  ownerKind = "project",
  ownerId,
  jobName = "",
  companyName = "",
  apiBase = "/api",
  n0 = (v) => String(v),
}) {
  const vp = vendorPricing || {};
  const assign = vp.assign || {}; // { rowKey: [vendorId] }
  const award = vp.award || {}; // { rowKey: { vendorId, vendorName, price, sku, quoteId, at } }

  const api = useCallback(
    async (path, opts = {}) => {
      const r = await fetch(apiBase + path, {
        ...opts,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "X-Tenant-ID": localStorage.getItem("tenantId"),
        },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || j.message || `Request failed (${r.status})`);
      return j;
    },
    [apiBase],
  );

  // ── vendors + requests ────────────────────────────────────────────────────
  const [vendors, setVendors] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loadErr, setLoadErr] = useState("");
  const [toast, setToast] = useState(null);
  const toastT = useRef(null);
  const say = (text, ok = true) => {
    setToast({ text, ok });
    clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setToast(null), 4500);
  };
  useEffect(() => {
    api("/accounts/vendors")
      .then((d) => setVendors((Array.isArray(d) ? d : d.vendors || d.accounts || []).filter((v) => v && v._id)))
      .catch((e) => setLoadErr(`Vendors: ${e.message}`));
  }, [api]);
  const loadQuotes = useCallback(() => {
    if (!takeoffId) return;
    api(`/vendor-quotes?takeoffId=${takeoffId}`)
      .then((d) => setQuotes(d.quotes || []))
      .catch((e) => setLoadErr(`Price requests: ${e.message}`));
  }, [api, takeoffId]);
  useEffect(() => {
    loadQuotes();
    const f = () => document.visibilityState === "visible" && loadQuotes();
    document.addEventListener("visibilitychange", f);
    const t = setInterval(loadQuotes, 60000);
    return () => {
      document.removeEventListener("visibilitychange", f);
      clearInterval(t);
    };
  }, [loadQuotes]);
  const vendorById = useMemo(() => new Map(vendors.map((v) => [String(v._id), v])), [vendors]);
  const vName = (id) => vendorLabel(vendorById.get(String(id))) || quotes.find((q) => String(q.vendor) === String(id))?.vendorName || "Vendor";

  // ── the material rows, with what we know about each item ─────────────────
  const items = useMemo(() => {
    // a section number that is also a cost code ("09 21 16") reads the same as that cost code: "09 21 16 – Gypsum board"
    const ccByCode = new Map();
    for (const c of ccIndex ? ccIndex.values() : []) if (c?.code) ccByCode.set(String(c.code).replace(/\s+/g, " ").trim(), c);
    const ccText = (c) => [c.code, c.name].filter(Boolean).join(" – ");
    const meta = new Map();
    for (const m of mappings)
      for (const it of m.items || []) {
        if (it.category === "L") continue;
        const k = matchKey(it.itemCode, it.size);
        if (!meta.has(k)) meta.set(k, it);
      }
    return rows.map((t) => {
      const it = meta.get(matchKey(t.itemCode, t.size)) || {};
      const db = it.itemDatabaseId ? catalogById.get(String(it.itemDatabaseId)) : null;
      const ccId = it.costCode || idOf(db?.costCode);
      const cc = ccId && ccIndex ? ccIndex.get(String(ccId)) : null;
      const sec = (it.sectionNumber || db?.sectionNumber || "").replace(/\s+/g, " ").trim();
      const section =
        (sec && (ccByCode.get(sec) ? ccText(ccByCode.get(sec)) : sec)) ||
        (cc ? ccText(cc) : "") ||
        it.costCodeLabel ||
        "No section";
      return {
        ...t,
        key: keyOf(t),
        itemDatabaseId: it.itemDatabaseId || db?._id || null,
        uom: it.uom || db?.uom || t.unit,
        rate: Number(it.rate) || 0,
        vendorId: it.vendorId || null,
        vendorName: it.vendorName || "",
        section,
        db,
        defaultVendor: idOf(db?.defaultVendor),
        skuFor: (vid) => (db?.vendorPrices || []).find((p) => idOf(p.vendor) === String(vid))?.partNumber || "",
        dbPriceFor: (vid) => (db?.vendorPrices || []).find((p) => idOf(p.vendor) === String(vid))?.price ?? null,
      };
    });
  }, [rows, mappings, catalogById, ccIndex]);

  const sections = useMemo(() => {
    const m = new Map();
    for (const r of items) m.set(r.section, (m.get(r.section) || 0) + 1);
    return [...m.entries()].sort((a, b) => (a[0] === "No section") - (b[0] === "No section") || a[0].localeCompare(b[0], undefined, { numeric: true }));
  }, [items]);

  // prices received: { rowKey: { vendorId: { price, unavailable, note, vendorSku, quoteId, status } } }
  const got = useMemo(() => {
    const out = {};
    for (const q of quotes)
      for (const l of q.lines || []) {
        (out[l.key] ||= {})[String(q.vendor)] = {
          price: l.price,
          unavailable: l.unavailable,
          note: l.note,
          vendorSku: l.vendorSku || l.sku || "",
          quoteId: q._id,
          status: q.status,
        };
      }
    return out;
  }, [quotes]);

  // ── filters + selection ──────────────────────────────────────────────────
  const [section, setSection] = useState("all");
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all"); // all | unassigned | priced | unpriced
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter(
      (r) =>
        (section === "all" || r.section === section) &&
        (!s || `${r.itemCode} ${r.title} ${r.size} ${r.section}`.toLowerCase().includes(s)) &&
        (only === "all" ||
          (only === "unassigned" && !(assign[r.key] || []).length) ||
          (only === "priced" && Object.values(got[r.key] || {}).some((x) => x.price != null)) ||
          (only === "unpriced" && !award[r.key])),
    );
  }, [items, section, q, only, assign, got, award]);
  const [sel, setSel] = useState(() => new Set());
  const allShown = shown.length > 0 && shown.every((r) => sel.has(r.key));
  const toggleAll = () =>
    setSel((p) => {
      const n = new Set(p);
      if (allShown) shown.forEach((r) => n.delete(r.key));
      else shown.forEach((r) => n.add(r.key));
      return n;
    });
  const toggle = (k) =>
    setSel((p) => {
      const n = new Set(p);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  const targets = sel.size ? items.filter((r) => sel.has(r.key)) : shown;

  const writeAssign = (fn) => {
    const next = { ...assign };
    fn(next);
    for (const k of Object.keys(next)) if (!next[k]?.length) delete next[k];
    setVendorPricing({ ...vp, assign: next });
  };
  const addVendors = (ids, mode = "add") => {
    writeAssign((a) => {
      for (const r of targets) {
        const cur = a[r.key] || [];
        a[r.key] =
          mode === "replace" ? [...ids] : mode === "remove" ? cur.filter((x) => !ids.includes(x)) : [...new Set([...cur, ...ids])];
      }
    });
    say(
      `${mode === "remove" ? "Removed from" : mode === "replace" ? "Set on" : "Added to"} ${targets.length} item${targets.length === 1 ? "" : "s"}.`,
    );
  };
  const addDefaultVendor = () => {
    let n = 0,
      none = 0;
    writeAssign((a) => {
      for (const r of targets) {
        if (!r.defaultVendor) {
          none++;
          continue;
        }
        const cur = a[r.key] || [];
        if (!cur.includes(r.defaultVendor)) {
          a[r.key] = [...cur, r.defaultVendor];
          n++;
        }
      }
    });
    say(
      `Default vendor added to ${n} item${n === 1 ? "" : "s"}${none ? ` — ${none} ${none === 1 ? "has" : "have"} no default vendor in the item database` : ""}.`,
      !none || n > 0,
    );
  };
  const removeOne = (key, vid) => writeAssign((a) => (a[key] = (a[key] || []).filter((x) => x !== vid)));

  // ── modals ───────────────────────────────────────────────────────────────
  const [picker, setPicker] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [compare, setCompare] = useState(false);
  const [entry, setEntry] = useState(null); // quote being typed in

  const vendorsWithItems = useMemo(() => {
    const m = new Map();
    for (const r of items) for (const v of assign[r.key] || []) m.set(v, [...(m.get(v) || []), r]);
    return [...m.entries()].map(([id, list]) => ({ id, list, vendor: vendorById.get(id) }));
  }, [items, assign, vendorById]);

  const totalAssigned = items.filter((r) => (assign[r.key] || []).length).length;
  const pricedQuotes = quotes.filter((x) => (x.lines || []).some((l) => l.price != null));

  return (
    <div className="mx-auto max-w-7xl space-y-3">
      {/* top bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-gray-900/70 p-3">
        <select
          value={section}
          onChange={(e) => setSection(e.target.value)}
          className="h-9 max-w-[280px] rounded-lg border border-white/10 bg-gray-950 px-2 text-sm text-gray-100"
          aria-label="Section"
        >
          <option value="all">All sections ({items.length})</option>
          {sections.map(([s, n]) => (
            <option key={s} value={s}>
              {s} ({n})
            </option>
          ))}
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search material…"
          className="h-9 w-48 rounded-lg border border-white/10 bg-gray-950 px-3 text-sm text-gray-100 placeholder-gray-500"
        />
        <div className="flex rounded-lg bg-gray-950 p-0.5 text-xs">
          {[
            ["all", "All"],
            ["unassigned", "No vendor"],
            ["priced", "Priced"],
            ["unpriced", "Not chosen"],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setOnly(k)}
              className={cls("rounded-md px-2.5 py-1.5", only === k ? "bg-white/10 text-white" : "text-gray-400 hover:text-gray-200")}
            >
              {l}
            </button>
          ))}
        </div>
        <span className="flex-1" />
        <button
          onClick={() => setCompare(true)}
          disabled={!pricedQuotes.length}
          className="h-9 rounded-lg bg-white/10 px-3 text-sm font-semibold text-white hover:bg-white/15 disabled:opacity-40"
          title={pricedQuotes.length ? "" : "No prices back from vendors yet"}
        >
          Compare & choose{pricedQuotes.length ? ` · ${pricedQuotes.length}` : ""}
        </button>
        <button
          onClick={() => setSendOpen(true)}
          disabled={!vendorsWithItems.length}
          className="h-9 rounded-lg bg-orange-500 px-3 text-sm font-semibold text-white hover:bg-orange-400 disabled:opacity-40"
          title={vendorsWithItems.length ? "" : "Assign vendors to items first"}
        >
          Request prices{vendorsWithItems.length ? ` · ${vendorsWithItems.length} vendor${vendorsWithItems.length === 1 ? "" : "s"}` : ""}
        </button>
      </div>

      {/* selection bar */}
      <div className="flex flex-wrap items-center gap-2 px-1 text-sm">
        <span className="text-gray-400">
          {sel.size ? (
            <>
              <b className="text-white">{sel.size}</b> selected
            </>
          ) : (
            <>Acting on the <b className="text-gray-200">{shown.length}</b> shown — tick items to narrow it</>
          )}
        </span>
        <button onClick={() => setPicker(true)} disabled={!targets.length} className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15 disabled:opacity-40">
          Assign vendors…
        </button>
        <button onClick={addDefaultVendor} disabled={!targets.length} className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15 disabled:opacity-40" title="Adds each item's default vendor from the item database">
          ★ Default vendor
        </button>
        {sel.size > 0 && (
          <button onClick={() => setSel(new Set())} className="rounded-lg px-2 py-1.5 text-xs text-gray-400 hover:text-white">
            Clear selection
          </button>
        )}
        <span className="flex-1" />
        <span className="text-xs text-gray-500">
          {totalAssigned}/{items.length} items have vendors · {Object.keys(award).length} prices chosen
        </span>
      </div>
      {loadErr && <div className="rounded-lg border border-red-500/30 bg-red-950/30 px-3 py-2 text-xs text-red-200">{loadErr}</div>}

      {/* the list */}
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-gray-900/70">
        <div className="grid grid-cols-[28px_minmax(0,2.2fr)_minmax(0,1fr)_90px_120px_minmax(0,2.4fr)_90px] items-center gap-2 bg-black/20 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
          <input type="checkbox" checked={allShown} onChange={toggleAll} className="h-4 w-4 accent-orange-500" aria-label="Select all shown" />
          <div>Material</div>
          <div>Section</div>
          <div className="text-right">Qty</div>
          <div className="text-right">Price · vendor</div>
          <div>Vendors asked</div>
          <div className="text-right">Cost</div>
        </div>
        {items.length === 0 && <p className="p-8 text-center text-sm text-gray-500">No material priced yet.</p>}
        {shown.map((r) => {
          const vs = assign[r.key] || [];
          const aw = award[r.key];
          const prices = got[r.key] || {};
          const best = Math.min(...Object.values(prices).filter((x) => x.price != null && !x.unavailable).map((x) => Number(x.price)));
          return (
            <div
              key={r.key}
              className={cls(
                "grid grid-cols-[28px_minmax(0,2.2fr)_minmax(0,1fr)_90px_120px_minmax(0,2.4fr)_90px] items-center gap-2 border-t border-white/5 px-3 py-2 text-[13px]",
                sel.has(r.key) && "bg-orange-500/[0.06]",
              )}
            >
              <input type="checkbox" checked={sel.has(r.key)} onChange={() => toggle(r.key)} className="h-4 w-4 accent-orange-500" aria-label={`Select ${r.itemCode}`} />
              <div className="min-w-0">
                <div className="truncate text-gray-100">
                  {r.title}
                  {r.size ? <span className="text-gray-500"> · {r.size}</span> : null}
                </div>
                <div className="truncate font-mono text-[11px] text-gray-500" title={r.assemblies.join(", ")}>
                  {r.itemCode} · {r.assemblies.length} assembl{r.assemblies.length === 1 ? "y" : "ies"}
                </div>
              </div>
              <div className="truncate text-[11px] text-gray-400" title={r.section}>
                {r.section}
              </div>
              <div className="text-right tabular-nums">
                <b>{n0(r.qty)}</b> <span className="text-gray-500">{r.unit}</span>
              </div>
              <div className="min-w-0 text-right">
                <div className="tabular-nums text-gray-100">{money2(r.rate)}</div>
                <div className={cls("truncate text-[10px]", aw ? "text-orange-300" : "text-gray-500")} title={r.vendorName}>
                  {aw ? `✓ ${aw.vendorName}` : r.vendorName || "item database"}
                </div>
              </div>
              <div className="flex min-w-0 flex-wrap gap-1">
                {vs.length === 0 && <span className="text-[11px] text-gray-600">—</span>}
                {vs.map((vid) => {
                  const p = prices[vid];
                  const chosen = aw && String(aw.vendorId) === vid;
                  const low = p && p.price != null && !p.unavailable && Number(p.price) === best && Object.keys(prices).length > 1;
                  return (
                    <span
                      key={vid}
                      className={cls(
                        "group inline-flex max-w-[200px] items-center gap-1 rounded-full px-2 py-0.5 text-[11px]",
                        chosen
                          ? "bg-orange-500 text-white"
                          : p?.unavailable
                            ? "bg-red-500/10 text-red-300 line-through"
                            : p?.price != null
                              ? "bg-emerald-500/15 text-emerald-200"
                              : p
                                ? "border border-orange-500/40 text-orange-200"
                                : "bg-white/10 text-gray-300",
                      )}
                      title={p?.note || (p ? STATUS[p.status]?.[0] : "Not requested yet")}
                    >
                      <span className="truncate">{vName(vid)}</span>
                      {p?.price != null && !p.unavailable && (
                        <b className="tabular-nums">
                          {money2(p.price)}
                          {low ? " ↓" : ""}
                        </b>
                      )}
                      {!chosen && (
                        <button onClick={() => removeOne(r.key, vid)} className="hidden text-gray-400 hover:text-white group-hover:inline" aria-label="Remove vendor">
                          ×
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
              <div className="text-right tabular-nums text-gray-200">{moneyT(r.amount)}</div>
            </div>
          );
        })}
      </div>

      {/* price requests */}
      {quotes.length > 0 && (
        <RequestList
          quotes={quotes}
          api={api}
          onChanged={(qq) => setQuotes((p) => p.map((x) => (x._id === qq._id ? qq : x)))}
          onDeleted={(id) => setQuotes((p) => p.filter((x) => x._id !== id))}
          onEnter={setEntry}
          jobName={jobName}
          companyName={companyName}
          say={say}
        />
      )}

      {picker && (
        <VendorPicker
          vendors={vendors}
          count={targets.length}
          preset={[...new Set(targets.flatMap((r) => assign[r.key] || []))]}
          onClose={() => setPicker(false)}
          onApply={(ids, mode) => {
            addVendors(ids, mode);
            setPicker(false);
          }}
        />
      )}
      {sendOpen && (
        <SendModal
          groups={vendorsWithItems}
          quotes={quotes}
          jobName={jobName}
          companyName={companyName}
          onClose={() => setSendOpen(false)}
          create={async (form, chosen) => {
            const requests = chosen.map((g) => ({
              vendor: g.id,
              vendorName: vendorLabel(g.vendor) || vName(g.id),
              email: form.emails[g.id] || "",
              lines: g.list.map((r) => ({
                key: r.key,
                itemCode: r.itemCode,
                itemDatabaseId: r.itemDatabaseId,
                description: r.title,
                size: r.size,
                uom: r.uom || r.unit,
                qty: Math.ceil(Number(r.qty) || 0),
                qtyUnit: r.unit,
                section: r.section,
                sku: r.skuFor(g.id),
              })),
            }));
            const d = await api("/vendor-quotes/batch", {
              method: "POST",
              body: JSON.stringify({
                takeoffId,
                [ownerKind === "opportunity" ? "opportunityId" : "projectId"]: ownerId,
                jobName: form.jobName,
                companyName,
                message: form.message,
                dueDate: form.dueDate || null,
                deliveryTo: form.deliveryTo,
                requests,
              }),
            });
            setQuotes((p) => {
              const m = new Map(p.map((x) => [x._id, x]));
              for (const x of d.quotes) m.set(x._id, x);
              return [...m.values()];
            });
            return d.quotes;
          }}
          api={api}
          onSent={(qq) => setQuotes((p) => p.map((x) => (x._id === qq._id ? qq : x)))}
          needSave={!takeoffId}
        />
      )}
      {compare && (
        <CompareModal
          items={items}
          quotes={pricedQuotes}
          award={award}
          onClose={() => setCompare(false)}
          onApply={async (picks, toDb) => {
            // picks: { rowKey: { vendorId, vendorName, price, sku, quoteId } }
            const at = new Date().toISOString();
            const nextAward = { ...award };
            const awards = [];
            for (const [k, p] of Object.entries(picks)) {
              const r = items.find((x) => x.key === k);
              if (!r) continue;
              nextAward[k] = { ...p, at };
              awards.push({ ...p, itemCode: r.itemCode, size: r.size, itemDatabaseId: r.itemDatabaseId, source: "vendor quote" });
            }
            for (const k of Object.keys(award)) if (!picks[k]) delete nextAward[k];
            onApply(awards, { ...vp, award: nextAward });
            const qids = [...new Set(awards.map((a) => a.quoteId).filter(Boolean))];
            for (const id of qids) api(`/vendor-quotes/${id}/applied`, { method: "POST" }).then((d) => setQuotes((p) => p.map((x) => (x._id === id ? d.quote : x)))).catch(() => {});
            let dbNote = "";
            if (toDb) {
              try {
                const lines = awards
                  .filter((a) => a.itemDatabaseId)
                  .map((a) => ({ itemDatabaseId: a.itemDatabaseId, vendor: a.vendorId, price: a.price, partNumber: a.sku }));
                const d = await api("/vendor-quotes/apply-to-database", { method: "POST", body: JSON.stringify({ lines }) });
                dbNote = ` Item database: ${d.updated} updated, ${d.added} added.`;
              } catch (e) {
                dbNote = ` Item database not updated: ${e.message}`;
              }
            }
            setCompare(false);
            say(`${awards.length} price${awards.length === 1 ? "" : "s"} applied to the estimate — they go to the budget when you publish.${dbNote}`);
          }}
        />
      )}
      {entry && (
        <EntryModal
          quote={entry}
          api={api}
          onClose={() => setEntry(null)}
          onSaved={(qq) => {
            setQuotes((p) => p.map((x) => (x._id === qq._id ? qq : x)));
            setEntry(null);
            say(`Prices saved for ${qq.vendorName}.`);
          }}
        />
      )}
      {toast && (
        <div className={cls("fixed bottom-6 left-1/2 z-[1500] -translate-x-1/2 rounded-xl px-4 py-2.5 text-sm font-medium shadow-2xl", toast.ok ? "bg-emerald-600 text-white" : "bg-amber-600 text-white")}>
          {toast.text}
        </div>
      )}
    </div>
  );
}

// ── Excel: the same sheet the vendor's page downloads / reads ────────────────
export function quoteToSheet(q, lines = q.lines) {
  const rows = lines.map((l, i) => ({
    Line: i + 1,
    Ref: l.key,
    Section: l.section || "",
    "Item code": l.itemCode,
    "Your SKU": l.vendorSku || l.sku || "",
    Description: l.description,
    Size: l.size || "",
    Qty: l.qty,
    Unit: l.qtyUnit || l.uom,
    "Price per": l.uom || l.qtyUnit || "",
    "Unit price": l.price ?? "",
    Notes: l.note || "",
    "Not available (Y)": l.unavailable ? "Y" : "",
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [{ wch: 5 }, { wch: 6, hidden: true }, { wch: 22 }, { wch: 16 }, { wch: 16 }, { wch: 44 }, { wch: 10 }, { wch: 10 }, { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 32 }, { wch: 16 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Pricing");
  XLSX.writeFile(wb, `Pricing - ${q.jobName || "request"} - ${q.vendorName || ""}`.replace(/[\\/:*?"<>|]/g, "").slice(0, 120) + ".xlsx");
}
// a vendor's spreadsheet → { [key]: { price, vendorSku, note, unavailable } }  (matched by Ref, item code, SKU or line #)
export function readSheet(buf, lines) {
  const wb = XLSX.read(new Uint8Array(buf), { type: "array" });
  const raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
  const norm = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k.toLowerCase().replace(/[^a-z]/g, ""), v]));
  const out = {};
  let miss = 0;
  for (const o of raw) {
    const r = norm(o);
    const price = r.unitprice ?? r.price ?? r.cost ?? "";
    const code = String(r.itemcode ?? "").trim();
    let l =
      (r.ref && lines.find((z) => z.key === String(r.ref))) ||
      (code && (lines.find((z) => z.itemCode === code && (!r.size || String(z.size) === String(r.size).trim())) || lines.find((z) => z.itemCode === code))) ||
      (r.yoursku && lines.find((z) => (z.vendorSku || z.sku) && (z.vendorSku || z.sku) === String(r.yoursku).trim())) ||
      (r.line && lines[Number(r.line) - 1]) ||
      null;
    if (!l) {
      if (String(price).trim() !== "") miss++;
      continue;
    }
    const p = String(price).replace(/[$,\s]/g, "");
    const na = r.notavailabley ?? r.notavailable;
    out[l.key] = {
      price: p !== "" && !isNaN(p) ? Number(p) : null,
      vendorSku: String(r.yoursku ?? "").trim() || l.vendorSku || l.sku || "",
      note: r.notes !== undefined ? String(r.notes) : l.note || "",
      unavailable: na !== undefined ? /^(y|yes|x|true|1)$/i.test(String(na).trim()) : !!l.unavailable,
    };
  }
  return { byKey: out, matched: Object.keys(out).length, miss };
}

function Modal({ title, sub, onClose, children, footer, wide }) {
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/60 p-4" onMouseDown={onClose}>
      <div
        className={cls("flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-2xl", wide ? "max-w-6xl" : "max-w-xl")}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-white/10 px-5 py-3">
          <div className="flex-1">
            <div className="text-sm font-semibold text-white">{title}</div>
            {sub && <div className="text-[11px] text-gray-500">{sub}</div>}
          </div>
          <button onClick={onClose} className="rounded-lg px-2 py-1 text-gray-400 hover:bg-white/10 hover:text-white" aria-label="Close">
            ✕
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">{children}</div>
        {footer && <div className="flex flex-wrap items-center gap-2 border-t border-white/10 bg-black/20 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

function VendorPicker({ vendors, count, preset, onClose, onApply }) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(() => new Set(preset));
  const list = vendors
    .filter((v) => !q.trim() || `${vendorLabel(v)} ${vendorEmail(v)}`.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => picked.has(String(b._id)) - picked.has(String(a._id)) || vendorLabel(a).localeCompare(vendorLabel(b)));
  const ids = [...picked];
  return (
    <Modal
      title={`Vendors for ${count} item${count === 1 ? "" : "s"}`}
      sub="Pick the vendors you'd order this group from"
      onClose={onClose}
      footer={
        <>
          <span className="text-xs text-gray-400">{ids.length} picked</span>
          <span className="flex-1" />
          <button disabled={!ids.length} onClick={() => onApply(ids, "remove")} className="rounded-lg px-3 py-2 text-xs text-gray-300 hover:bg-white/10 disabled:opacity-40">
            Remove these
          </button>
          <button disabled={!ids.length} onClick={() => onApply(ids, "replace")} className="rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/15 disabled:opacity-40">
            Replace
          </button>
          <button disabled={!ids.length} onClick={() => onApply(ids, "add")} className="rounded-lg bg-orange-500 px-3 py-2 text-xs font-semibold text-white hover:bg-orange-400 disabled:opacity-40">
            Add to items
          </button>
        </>
      }
    >
      <div className="p-4">
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendors…" className="mb-3 h-9 w-full rounded-lg border border-white/10 bg-gray-950 px-3 text-sm text-gray-100 placeholder-gray-500" />
        {!vendors.length && <p className="py-6 text-center text-sm text-gray-500">No vendors found in Accounts.</p>}
        <div className="space-y-1">
          {list.map((v) => {
            const id = String(v._id);
            return (
              <label key={id} className={cls("flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2", picked.has(id) ? "bg-orange-500/10" : "hover:bg-white/5")}>
                <input
                  type="checkbox"
                  checked={picked.has(id)}
                  onChange={() =>
                    setPicked((p) => {
                      const n = new Set(p);
                      n.has(id) ? n.delete(id) : n.add(id);
                      return n;
                    })
                  }
                  className="h-4 w-4 accent-orange-500"
                />
                <span className="flex-1 text-sm text-gray-100">{vendorLabel(v)}</span>
                <span className="truncate text-[11px] text-gray-500">{vendorEmail(v) || "no e-mail on file"}</span>
              </label>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

function mailtoFor(q) {
  const subject = `Request for pricing — ${q.jobName || "material"}${q.companyName ? ` — ${q.companyName}` : ""}`;
  const body = [
    "Hello,",
    "",
    q.message || "Please price the material on the list at the link below.",
    "",
    `${q.lines?.length || 0} items: ${q.link}`,
    "",
    "Type your unit prices on the page, or download the list to Excel and upload it back. No login needed.",
    q.dueDate ? `Please reply by ${new Date(q.dueDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}.` : "",
    "",
    "Thank you,",
    localStorage.getItem("userDisplayName") || "",
    q.companyName || "",
  ]
    .filter((x, i, a) => !(x === "" && a[i - 1] === ""))
    .join("\n");
  return `mailto:${encodeURIComponent(q.email || "")}?${q.cc?.length ? `cc=${encodeURIComponent(q.cc.join(","))}&` : ""}subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function SendModal({ groups, quotes, jobName, companyName, onClose, create, api, onSent, needSave }) {
  const [form, setForm] = useState(() => ({
    jobName,
    dueDate: inDays(5),
    deliveryTo: "",
    message: `Please quote the material below for ${jobName || "this job"}. Prices per the unit shown; note lead times and any substitutes.`,
    emails: Object.fromEntries(groups.map((g) => [g.id, quotes.find((x) => String(x.vendor) === g.id && x.status !== "closed")?.email || vendorEmail(g.vendor)])),
  }));
  const [chosen, setChosen] = useState(() => new Set(groups.map((g) => g.id)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [made, setMade] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const markSent = async (q) => {
    try {
      const d = await api(`/vendor-quotes/${q._id}/sent`, { method: "POST" });
      onSent(d.quote);
      setMade((m) => m.map((x) => (x._id === q._id ? d.quote : x)));
    } catch {}
  };
  return (
    <Modal
      title={made ? "Links ready — send them" : "Request prices"}
      sub={made ? "Each vendor gets a private link with only their items. Opening your e-mail marks it sent." : "One private link per vendor, with only the items assigned to them"}
      onClose={onClose}
      wide={!!made}
      footer={
        made ? (
          <>
            <span className="text-xs text-gray-400">The links stay the same if you ask the same vendor again — new items are added to their list.</span>
            <span className="flex-1" />
            <button onClick={onClose} className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-400">
              Done
            </button>
          </>
        ) : (
          <>
            {err && <span className="text-xs text-red-300">{err}</span>}
            <span className="flex-1" />
            <button onClick={onClose} className="rounded-lg px-3 py-2 text-sm text-gray-300 hover:bg-white/10">
              Cancel
            </button>
            <button
              disabled={busy || !chosen.size || needSave}
              onClick={async () => {
                setBusy(true);
                setErr("");
                try {
                  setMade(await create(form, groups.filter((g) => chosen.has(g.id))));
                } catch (e) {
                  setErr(e.message);
                } finally {
                  setBusy(false);
                }
              }}
              className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-400 disabled:opacity-40"
            >
              {busy ? "Creating…" : `Create ${chosen.size} link${chosen.size === 1 ? "" : "s"}`}
            </button>
          </>
        )
      }
    >
      {made ? (
        <div className="divide-y divide-white/5">
          {made.map((q) => (
            <div key={q._id} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <div className="min-w-[180px] flex-1">
                <div className="text-sm font-semibold text-white">{q.vendorName}</div>
                <div className="text-[11px] text-gray-500">
                  {q.lines.length} items · {q.email || <span className="text-amber-300">no e-mail — copy the link</span>}
                </div>
              </div>
              <span className={cls("rounded-full px-2 py-0.5 text-[11px] font-medium", STATUS[q.status]?.[1])}>{STATUS[q.status]?.[0]}</span>
              <a href={mailtoFor(q)} onClick={() => markSent(q)} className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-400">
                ✉ E-mail
              </a>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(q.link);
                  markSent(q);
                }}
                className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
              >
                Copy link
              </button>
              <button onClick={() => quoteToSheet(q)} className="rounded-lg bg-white/10 px-3 py-1.5 text-xs text-gray-200 hover:bg-white/15" title="To attach to your own e-mail">
                Excel
              </button>
              <a href={q.link} target="_blank" rel="noreferrer" className="rounded-lg px-2 py-1.5 text-xs text-gray-400 hover:text-white">
                Preview ↗
              </a>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3 p-5 text-sm">
          {needSave && <div className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200">Save the takeoff first — the requests are kept with it.</div>}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500">Job name (shown to vendors)</span>
              <input value={form.jobName} onChange={(e) => set("jobName", e.target.value)} className="h-9 w-full rounded-lg border border-white/10 bg-gray-950 px-3 text-gray-100" />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500">Reply by</span>
              <input type="date" value={form.dueDate} onChange={(e) => set("dueDate", e.target.value)} className="h-9 w-full rounded-lg border border-white/10 bg-gray-950 px-3 text-gray-100 [color-scheme:dark]" />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500">Deliver to (optional)</span>
            <input value={form.deliveryTo} onChange={(e) => set("deliveryTo", e.target.value)} placeholder="Site address" className="h-9 w-full rounded-lg border border-white/10 bg-gray-950 px-3 text-gray-100 placeholder-gray-600" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500">Message</span>
            <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} className="w-full rounded-lg border border-white/10 bg-gray-950 px-3 py-2 text-gray-100" />
          </label>
          <div>
            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-500">Vendors</span>
            <div className="divide-y divide-white/5 rounded-xl border border-white/10">
              {groups.map((g) => {
                const open = quotes.find((x) => String(x.vendor) === g.id && x.status !== "closed");
                return (
                  <div key={g.id} className="flex items-center gap-3 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={chosen.has(g.id)}
                      onChange={() =>
                        setChosen((p) => {
                          const n = new Set(p);
                          n.has(g.id) ? n.delete(g.id) : n.add(g.id);
                          return n;
                        })
                      }
                      className="h-4 w-4 accent-orange-500"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-gray-100">{vendorLabel(g.vendor)}</div>
                      <div className="text-[11px] text-gray-500">
                        {g.list.length} items{open ? ` · already asked (${STATUS[open.status]?.[0].toLowerCase()}) — updates that request` : ""}
                        {` · ${g.list.filter((r) => r.skuFor(g.id)).length} with SKU`}
                      </div>
                    </div>
                    <input
                      value={form.emails[g.id] || ""}
                      onChange={(e) => setForm((f) => ({ ...f, emails: { ...f.emails, [g.id]: e.target.value } }))}
                      placeholder="vendor@email.com"
                      className="h-8 w-56 rounded-lg border border-white/10 bg-gray-950 px-2 text-xs text-gray-100 placeholder-gray-600"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function RequestList({ quotes, api, onChanged, onDeleted, onEnter, say }) {
  const act = async (q, path, method = "POST") => {
    try {
      const d = await api(`/vendor-quotes/${q._id}${path}`, { method });
      if (method === "DELETE") onDeleted(q._id);
      else onChanged(d.quote);
    } catch (e) {
      say(e.message, false);
    }
  };
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-gray-900/70">
      <div className="flex items-center gap-2 bg-black/20 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Price requests</div>
      {quotes.map((q) => {
        const priced = q.lines.filter((l) => l.price != null).length;
        const na = q.lines.filter((l) => l.unavailable).length;
        const total = q.lines.reduce((s, l) => s + (l.price != null && !l.unavailable ? l.price * (l.qty || 0) : 0), 0) + (Number(q.response?.delivery) || 0);
        const late = q.dueDate && !q.submittedAt && new Date(q.dueDate) < new Date(new Date().toDateString());
        return (
          <div key={q._id} className="flex flex-wrap items-center gap-3 border-t border-white/5 px-4 py-2.5 text-[13px]">
            <div className="min-w-[180px] flex-1">
              <div className="font-semibold text-gray-100">{q.vendorName}</div>
              <div className="text-[11px] text-gray-500">
                {q.email || "no e-mail"} · {q.lines.length} items
                {q.dueDate ? <span className={late ? " text-red-300" : ""}> · due {day(q.dueDate)}</span> : null}
                {q.response?.enteredBy && q.response.enteredBy !== "vendor" ? ` · entered by ${q.response.enteredBy}` : ""}
                {q.response?.notes ? ` · “${q.response.notes.slice(0, 60)}${q.response.notes.length > 60 ? "…" : ""}”` : ""}
              </div>
            </div>
            <span className={cls("rounded-full px-2 py-0.5 text-[11px] font-medium", STATUS[q.status]?.[1])}>
              {STATUS[q.status]?.[0]}
              {q.submittedAt ? ` ${day(q.submittedAt)}` : ""}
            </span>
            <span className="w-28 text-right text-xs text-gray-400">
              {priced}/{q.lines.length} priced{na ? ` · ${na} N/A` : ""}
            </span>
            <span className="w-24 text-right font-semibold tabular-nums text-gray-100">{priced ? moneyT(total) : "—"}</span>
            <div className="flex items-center gap-1">
              <a href={mailtoFor(q)} onClick={() => q.status === "draft" && act(q, "/sent")} className="rounded-md px-2 py-1 text-xs text-gray-300 hover:bg-white/10" title="E-mail the link">
                ✉
              </a>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(q.link);
                  say("Link copied.");
                  if (q.status === "draft") act(q, "/sent");
                }}
                className="rounded-md px-2 py-1 text-xs text-gray-300 hover:bg-white/10"
              >
                Copy link
              </button>
              <button onClick={() => onEnter(q)} className="rounded-md px-2 py-1 text-xs text-gray-300 hover:bg-white/10" title="Type in or import the vendor's prices">
                Enter prices
              </button>
              <button onClick={() => quoteToSheet(q)} className="rounded-md px-2 py-1 text-xs text-gray-300 hover:bg-white/10">
                Excel
              </button>
              {q.status === "closed" ? (
                <button onClick={() => act(q, "/reopen")} className="rounded-md px-2 py-1 text-xs text-gray-300 hover:bg-white/10">
                  Reopen
                </button>
              ) : (
                <button onClick={() => act(q, "/close")} className="rounded-md px-2 py-1 text-xs text-gray-300 hover:bg-white/10" title="The vendor can't change prices any more">
                  Close
                </button>
              )}
              <button
                onClick={() => window.confirm(`Delete the request to ${q.vendorName}? Its link stops working.`) && act(q, "", "DELETE")}
                className="rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-red-500/10 hover:text-red-300"
                aria-label="Delete request"
              >
                🗑
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EntryModal({ quote, api, onClose, onSaved }) {
  const [lines, setLines] = useState(() => quote.lines.map((l) => ({ ...l })));
  const [resp, setResp] = useState(() => ({ name: quote.response?.name || "", delivery: quote.response?.delivery ?? "", notes: quote.response?.notes || "" }));
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k, f, v) => setLines((p) => p.map((l) => (l.key === k ? { ...l, [f]: v } : l)));
  const total = lines.reduce((s, l) => s + (l.price !== "" && l.price != null && !l.unavailable ? Number(l.price) * (l.qty || 0) : 0), 0);
  return (
    <Modal
      wide
      title={`Prices from ${quote.vendorName}`}
      sub="Type them in, or import the spreadsheet they sent back (the Excel from this request, or any sheet with item codes and a price column)"
      onClose={onClose}
      footer={
        <>
          <button onClick={() => fileRef.current?.click()} className="rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/15">
            ⬆ Import Excel
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              const rd = new FileReader();
              rd.onload = () => {
                try {
                  const r = readSheet(rd.result, lines);
                  setLines((p) => p.map((l) => (r.byKey[l.key] ? { ...l, ...r.byKey[l.key] } : l)));
                  setMsg(r.matched ? `Read ${r.matched} lines${r.miss ? ` — ${r.miss} rows didn't match` : ""}. Check, then save.` : "No rows matched — keep the Ref or Item code column.");
                } catch (er) {
                  setMsg(`Couldn't read that file: ${er.message}`);
                }
              };
              rd.readAsArrayBuffer(f);
            }}
          />
          {msg && <span className="text-xs text-gray-300">{msg}</span>}
          <span className="flex-1" />
          <span className="text-sm text-gray-300">
            Total <b className="text-orange-300">{moneyT(total + (Number(resp.delivery) || 0))}</b>
          </span>
          <button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const d = await api(`/vendor-quotes/${quote._id}`, {
                  method: "PUT",
                  body: JSON.stringify({
                    lines: lines.map((l) => ({ key: l.key, price: l.price === "" ? null : l.price, vendorSku: l.vendorSku, note: l.note, unavailable: l.unavailable })),
                    response: resp,
                    markSubmitted: true,
                  }),
                });
                onSaved(d.quote);
              } catch (e) {
                setMsg(e.message);
                setBusy(false);
              }
            }}
            className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-400 disabled:opacity-40"
          >
            {busy ? "Saving…" : "Save prices"}
          </button>
        </>
      }
    >
      <table className="w-full text-[13px]">
        <thead className="sticky top-0 bg-gray-900 text-[10px] uppercase tracking-wider text-gray-500">
          <tr>
            <th className="px-3 py-2 text-left">Item</th>
            <th className="px-2 py-2 text-left">Their SKU</th>
            <th className="px-2 py-2 text-right">Qty</th>
            <th className="px-2 py-2 text-right">Unit price</th>
            <th className="px-2 py-2 text-left">Note</th>
            <th className="px-2 py-2">N/A</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.key} className={cls("border-t border-white/5", l.unavailable && "opacity-50")}>
              <td className="px-3 py-1.5">
                <div className="text-gray-100">
                  {l.description}
                  {l.size ? <span className="text-gray-500"> · {l.size}</span> : null}
                </div>
                <div className="font-mono text-[11px] text-gray-500">{l.itemCode}</div>
              </td>
              <td className="px-2">
                <input value={l.vendorSku || ""} onChange={(e) => set(l.key, "vendorSku", e.target.value)} placeholder={l.sku || ""} className="h-8 w-28 rounded-md border border-white/10 bg-gray-950 px-2 text-xs text-gray-100" />
              </td>
              <td className="px-2 text-right tabular-nums text-gray-300">
                {l.qty} <span className="text-gray-500">{l.qtyUnit || l.uom}</span>
              </td>
              <td className="px-2 text-right">
                <input
                  inputMode="decimal"
                  value={l.price ?? ""}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[$,\s]/g, "");
                    set(l.key, "price", v === "" ? "" : isNaN(v) ? l.price : v);
                  }}
                  disabled={l.unavailable}
                  className="h-8 w-24 rounded-md border border-white/10 bg-gray-950 px-2 text-right text-xs text-gray-100"
                />
                <div className="text-[10px] text-gray-500">per {l.uom || l.qtyUnit}</div>
              </td>
              <td className="px-2">
                <input value={l.note || ""} onChange={(e) => set(l.key, "note", e.target.value)} className="h-8 w-full rounded-md border border-white/10 bg-gray-950 px-2 text-xs text-gray-100" />
              </td>
              <td className="px-2 text-center">
                <input type="checkbox" checked={!!l.unavailable} onChange={(e) => set(l.key, "unavailable", e.target.checked)} className="h-4 w-4 accent-orange-500" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid grid-cols-3 gap-3 border-t border-white/10 p-4 text-sm">
        <input value={resp.name} onChange={(e) => setResp((r) => ({ ...r, name: e.target.value }))} placeholder="Quoted by (name)" className="h-9 rounded-lg border border-white/10 bg-gray-950 px-3 text-gray-100 placeholder-gray-600" />
        <input value={resp.delivery} onChange={(e) => setResp((r) => ({ ...r, delivery: e.target.value }))} placeholder="Delivery charge $" className="h-9 rounded-lg border border-white/10 bg-gray-950 px-3 text-gray-100 placeholder-gray-600" />
        <input value={resp.notes} onChange={(e) => setResp((r) => ({ ...r, notes: e.target.value }))} placeholder="Terms / notes" className="h-9 rounded-lg border border-white/10 bg-gray-950 px-3 text-gray-100 placeholder-gray-600" />
      </div>
    </Modal>
  );
}

function CompareModal({ items, quotes, award, onClose, onApply }) {
  const vendorCols = useMemo(() => {
    const m = new Map();
    for (const q of quotes) if (!m.has(String(q.vendor))) m.set(String(q.vendor), q);
    return [...m.values()];
  }, [quotes]);
  const cell = (r, q) => q.lines.find((l) => l.key === r.key);
  const rows = items.filter((r) => vendorCols.some((q) => cell(r, q)));
  const [picks, setPicks] = useState(() => {
    const p = {};
    for (const [k, a] of Object.entries(award)) p[k] = { vendorId: a.vendorId, vendorName: a.vendorName, price: a.price, sku: a.sku, quoteId: a.quoteId };
    return p;
  });
  const [toDb, setToDb] = useState(false);
  const pick = (r, q) => {
    const l = cell(r, q);
    if (!l || l.price == null || l.unavailable) return;
    setPicks((p) => {
      const cur = p[r.key];
      if (cur && cur.quoteId === q._id) {
        const n = { ...p };
        delete n[r.key];
        return n;
      }
      return { ...p, [r.key]: { vendorId: String(q.vendor), vendorName: q.vendorName, price: Number(l.price), sku: l.vendorSku || l.sku || "", quoteId: q._id } };
    });
  };
  const lowest = (r) => {
    let best = null;
    for (const q of vendorCols) {
      const l = cell(r, q);
      if (l && l.price != null && !l.unavailable && (!best || Number(l.price) < best.p)) best = { p: Number(l.price), q };
    }
    return best;
  };
  const pickLowest = () =>
    setPicks((p) => {
      const n = { ...p };
      for (const r of rows) {
        const b = lowest(r);
        if (b) n[r.key] = { vendorId: String(b.q.vendor), vendorName: b.q.vendorName, price: b.p, sku: cell(r, b.q).vendorSku || cell(r, b.q).sku || "", quoteId: b.q._id };
      }
      return n;
    });
  const pickAllFrom = (q) =>
    setPicks((p) => {
      const n = { ...p };
      for (const r of rows) {
        const l = cell(r, q);
        if (l && l.price != null && !l.unavailable) n[r.key] = { vendorId: String(q.vendor), vendorName: q.vendorName, price: Number(l.price), sku: l.vendorSku || l.sku || "", quoteId: q._id };
      }
      return n;
    });
  const colTotal = (q) => rows.reduce((s, r) => {
    const l = cell(r, q);
    return s + (l && l.price != null && !l.unavailable ? l.price * r.qty : 0);
  }, 0);
  const chosenTotal = rows.reduce((s, r) => s + (picks[r.key] ? picks[r.key].price * r.qty : 0), 0);
  const n = Object.keys(picks).filter((k) => rows.some((r) => r.key === k)).length;
  return (
    <Modal
      wide
      title="Compare vendor prices"
      sub="Click a price to choose it for that item (click again to unchoose). Chosen prices become the estimate's rates and vendor, and go to the budget on publish."
      onClose={onClose}
      footer={
        <>
          <button onClick={pickLowest} className="rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/15">
            Choose lowest for all
          </button>
          <label className="flex items-center gap-2 text-xs text-gray-300">
            <input type="checkbox" checked={toDb} onChange={(e) => setToDb(e.target.checked)} className="h-4 w-4 accent-orange-500" />
            Also save these prices & SKUs to the item database (under each vendor)
          </label>
          <span className="flex-1" />
          <span className="text-sm text-gray-300">
            {n} chosen · <b className="text-orange-300">{moneyT(chosenTotal)}</b>
          </span>
          <button onClick={() => onApply(picks, toDb)} className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-400">
            Apply to estimate
          </button>
        </>
      }
    >
      <table className="w-full text-[13px]">
        <thead className="sticky top-0 z-10 bg-gray-900 text-[10px] uppercase tracking-wider text-gray-500">
          <tr>
            <th className="px-3 py-2 text-left">Item</th>
            <th className="px-2 py-2 text-right">Qty</th>
            <th className="px-2 py-2 text-right">Now</th>
            {vendorCols.map((q) => (
              <th key={q._id} className="px-2 py-2 text-right">
                <div className="truncate normal-case tracking-normal text-gray-200" title={q.vendorName}>
                  {q.vendorName}
                </div>
                <button onClick={() => pickAllFrom(q)} className="mt-0.5 rounded px-1 text-[10px] normal-case tracking-normal text-orange-300 hover:bg-white/5">
                  choose all
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const b = lowest(r);
            return (
              <tr key={r.key} className="border-t border-white/5">
                <td className="px-3 py-1.5">
                  <div className="text-gray-100">
                    {r.title}
                    {r.size ? <span className="text-gray-500"> · {r.size}</span> : null}
                  </div>
                  <div className="font-mono text-[11px] text-gray-500">
                    {r.itemCode} · {r.section}
                  </div>
                </td>
                <td className="px-2 text-right tabular-nums text-gray-300">
                  {Math.ceil(r.qty)} <span className="text-gray-500">{r.unit}</span>
                </td>
                <td className="px-2 text-right tabular-nums text-gray-500">{money2(r.rate)}</td>
                {vendorCols.map((q) => {
                  const l = cell(r, q);
                  const chosen = picks[r.key]?.quoteId === q._id;
                  const isLow = b && b.q._id === q._id;
                  if (!l) return <td key={q._id} className="px-2 text-right text-gray-700">not asked</td>;
                  if (l.unavailable) return <td key={q._id} className="px-2 text-right text-xs text-red-300" title={l.note}>N/A</td>;
                  if (l.price == null) return <td key={q._id} className="px-2 text-right text-xs text-gray-600">no price</td>;
                  return (
                    <td key={q._id} className="px-1 text-right">
                      <button
                        onClick={() => pick(r, q)}
                        title={[l.vendorSku && `SKU ${l.vendorSku}`, l.note].filter(Boolean).join(" · ")}
                        className={cls(
                          "w-full rounded-lg px-2 py-1 text-right tabular-nums transition-colors",
                          chosen ? "bg-orange-500 font-semibold text-white" : isLow ? "bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20" : "text-gray-200 hover:bg-white/10",
                        )}
                      >
                        {money2(l.price)}
                        {l.note ? <span className="ml-1 text-[10px] opacity-70">•</span> : null}
                        <div className={cls("text-[10px]", chosen ? "text-orange-100" : "text-gray-500")}>{moneyT(l.price * r.qty)}</div>
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        <tfoot className="sticky bottom-0 bg-gray-900">
          <tr className="border-t border-white/10 text-xs">
            <td className="px-3 py-2 text-gray-400" colSpan={3}>
              Material total (priced lines) · delivery
            </td>
            {vendorCols.map((q) => (
              <td key={q._id} className="px-2 py-2 text-right tabular-nums text-gray-200">
                <b>{moneyT(colTotal(q))}</b>
                <div className="text-[10px] text-gray-500">+ {moneyT(q.response?.delivery || 0)} del.</div>
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </Modal>
  );
}
